import { Box, HStack, Link, Spacer, Text } from "@chakra-ui/react";
import { useContext, useState } from "react";

import { RanaButton } from "../../lib/components/rana-button";
import { RanaForm, RanaFormContext } from "../../lib/rana-form";

function isEmailPossiblyValid(email: string) {
  // We don't care about the part before the @, but the last part of the domain should be at least 2 characters
  const emailParts = email.split("@");
  if (emailParts.length !== 2) {
    return false;
  }
  const domainParts = emailParts[1].split(".");
  if (domainParts.length < 2) {
    return false;
  }
  const lastPart = domainParts[domainParts.length - 1];
  return lastPart.length >= 2;
}

function rot13(c: string): string {
  // Convert all normal a-z and A-Z characters to ROT13
  return c.replace(/[a-zA-Z]/g, function (char) {
    const start = char <= "Z" ? 65 : 97;
    return String.fromCharCode(((char.charCodeAt(0) - start + 13) % 26) + start);
  });
}

function guessMyTenantCode(email: string): string | null {
  // We can assume a valid email here
  // Strip whitespace
  email = email.trim();
  const emailParts = email.split("@");
  const domainParts = emailParts[1];

  switch (rot13(domainParts)) {
    case "aryra-fpuhheznaf.ay":
      return rot13("araf"); // Nelen & Schuurmans
    case "uuax.ay":
      return rot13("uuax"); // HHNK
    case "uuqrysynaq.ay":
      return rot13("uuqrysynaq"); // HH Delfland
    case "jqbqrygn.ay":
      return rot13("jqbqrygn"); // WDO Delta
    case "nyzrer.ay":
      return rot13("nyzrer"); // Almere
    case "jngrefpuncyvzohet.ay":
      return rot13("jngrefpuncyvzohet"); // Waterschap Limburg
    case "tebavatra.ay":
      return rot13("tebavatra"); // Groningen
    case "nffra.ay":
      return rot13("nffra"); // Assen
    case "ohun.ay":
      return rot13("ohun"); // Buha
    case "onz.pbz":
      return rot13("onz-vasen-ay"); // BAM
    case "fnjn.pbz":
      return rot13("fnjn"); // Sawa
    case "pvivpba.ay":
      return rot13("pvivpba"); // Civicon
    case "arggra-jngrenqivrf.ay":
      return rot13("arggra-jngrenqivrf"); // Netten Wateradvies
    case "yrvqra.ay":
      return rot13("yrvqra"); // Leiden
    case "zbinerf.ay":
      return rot13("zbinerf"); // Movares
    case "unfxbavat.pbz":
      return rot13("unfxbavat"); // Haskoning
    case "chezreraq.ay":
      return rot13("chezreraq"); // Purmerend
    case "qraunnt.ay":
      return rot13("qraunnt"); // Den Haag
    case "iryfra.ay":
      return rot13("iryfra"); // Velsen
    case "cvcryvsr.pbz":
      return rot13("cvcryvsr"); // PipeLife
    case "shghervafvtug.pbz":
      return rot13("shgher-vafvtug"); // Future Insight
    case "beovf-ratvarrevat.ay":
      return rot13("agc"); // Orbis Engineering / NTP
    case "grpbgrp.pbz.ia":
      return rot13("grpbgrp"); // Tecotec
    default:
      return null;
  }
}

export function GuessMyTenantCode() {
  const { setPartialValues } = useContext(RanaFormContext);
  const [email, setEmail] = useState("");
  const [buttonClicked, setButtonClicked] = useState(false);
  const emailPossiblyValid = isEmailPossiblyValid(email);

  const tenantCodeGuess = emailPossiblyValid ? guessMyTenantCode(email) : null;

  const clickButton = () => {
    setButtonClicked(true);
    if (tenantCodeGuess !== null) {
      setPartialValues({ organisationCode: tenantCodeGuess });
    }
  };

  return (
    <Box w="full" display="flex" flexDir="column" my={6}>
      <p>Please enter your email address and we will try to determine your organisation code.</p>
      <Box mt={4} display="flex" flexDir="column" gap={4}>
        <RanaForm.Input
          type="email"
          field="email_address_dont_submit"
          label="Email address"
          placeholder="Email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (buttonClicked) {
              setButtonClicked(false);
            }
          }}
          onKeyDown={(e) => e.key === "Enter" && emailPossiblyValid && clickButton()}
        />
        {buttonClicked && tenantCodeGuess === null && (
          <Text h={11}>
            We could not determine your organisation code. Please contact{" "}
            <strong>
              <Link href="mailto:support@ranawaterintelligence.com">
                support@ranawaterintelligence.com
              </Link>
            </strong>
            .
          </Text>
        )}
        {buttonClicked && tenantCodeGuess !== null && (
          <Text h={11}>
            We have filled in your organisation code as <strong>{tenantCodeGuess}</strong>. If this
            is not correct, please contact{" "}
            <strong>
              <Link href="mailto:support@ranawaterintelligence.com">
                support@ranawaterintelligence.com
              </Link>
            </strong>
            .
          </Text>
        )}
        {!buttonClicked && <Box h={11}>&nbsp;</Box>}
        <HStack>
          <Spacer />
          <RanaButton variant="secondary" onClick={clickButton}>
            Request organisation code
          </RanaButton>
        </HStack>
      </Box>
    </Box>
  );
}
